import { useState } from "react";
import { useToast } from "../context/ToastContext";
import { X, CheckCircle2, AlertCircle, Info } from "lucide-react";
import FeedbackSheet from "./FeedbackSheet";

const TOAST_STYLES = {
  success: {
    bg: "var(--color-success-light)",
    border: "var(--color-success)",
    color: "var(--color-success-dark)",
    iconColor: "var(--color-success)",
  },
  error: {
    bg: "var(--color-danger-light)",
    border: "var(--color-danger)",
    color: "var(--color-danger)",
    iconColor: "var(--color-danger)",
  },
  info: {
    bg: "var(--color-surface)",
    border: "var(--color-border)",
    color: "var(--color-text)",
    iconColor: "var(--color-primary)",
  },
};

const ICON_MAP = { success: CheckCircle2, error: AlertCircle, info: Info };

export function ToastContainer() {
  const { toasts, dismissToast } = useToast();
  const [reportingToast, setReportingToast] = useState<{ message: string; context?: string } | null>(null);
  if (toasts.length === 0 && !reportingToast) return null;

  return (
    <>
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: "fixed",
        bottom: "calc(72px + env(safe-area-inset-bottom))",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        width: "calc(100% - 32px)",
        maxWidth: 400,
        pointerEvents: "none",
      }}
    >
      {toasts.map((t) => {
        const s = TOAST_STYLES[t.type];
        const Icon = ICON_MAP[t.type];
        return (
          <div
            key={t.id}
            role={t.type === "error" ? "alert" : "status"}
            style={{
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              borderRadius: 12,
              background: s.bg,
              border: `1.5px solid ${s.border}`,
              color: s.color,
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "inherit",
              boxShadow: "var(--shadow-md)",
              animation: "toast-slide-up 0.25s ease-out",
            }}
          >
            <Icon size={18} color={s.iconColor} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, lineHeight: 1.4, display: "flex", flexDirection: "column", gap: 2 }}>
              <span>{t.message}</span>
              {t.type === "error" && (
                <button
                  onClick={() => {
                    setReportingToast({ message: t.message, context: t.context });
                    dismissToast(t.id);
                  }}
                  style={{
                    alignSelf: "flex-start", background: "none", border: "none",
                    padding: 0, fontSize: 12, fontWeight: 600,
                    color: "inherit", opacity: 0.75, cursor: "pointer",
                    textDecoration: "underline", fontFamily: "inherit",
                  }}
                >
                  Report this
                </button>
              )}
            </div>
            {t.action && (
              <button
                onClick={() => { t.action!.onClick(); dismissToast(t.id); }}
                style={{
                  flexShrink: 0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 10px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "inherit",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  fontFamily: "inherit",
                }}
              >
                {t.action.label}
              </button>
            )}
            <button
              onClick={() => dismissToast(t.id)}
              aria-label="Dismiss"
              style={{
                flexShrink: 0,
                background: "none",
                border: "none",
                cursor: "pointer",
                opacity: 0.5,
                padding: 4,
                display: "flex",
                color: "inherit",
              }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
      <style>{`@keyframes toast-slide-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
    <FeedbackSheet
      open={!!reportingToast}
      onClose={() => setReportingToast(null)}
      category="error"
      context={reportingToast ? `Error: ${reportingToast.message}${reportingToast.context ? ` | ${reportingToast.context}` : ""}` : undefined}
      title="Report this error"
      prompt="What were you trying to do when this happened?"
      showSentiment={false}
    />
    </>
  );
}
